import React from 'react';
import { Camera, Wifi, Globe, Lock, Shield, BarChart2, CalendarCheck, Search, Mail, CircleCheckBig } from 'lucide-react';

const features = (lang) => [
  { icon: Wifi,         title: lang === 'tr' ? 'TCP Bağlantı Kontrolü'       : 'TCP Connection Check' },
  { icon: Globe,        title: lang === 'tr' ? 'DNS Çözümleme Takibi'        : 'DNS Resolution Tracking' },
  { icon: Shield,       title: lang === 'tr' ? 'HTTP Durum İzleme'           : 'HTTP Status Monitoring' },
  { icon: Lock,         title: lang === 'tr' ? 'SSL Sertifika İzleme'        : 'SSL Certificate Monitoring' },
  { icon: Search,       title: lang === 'tr' ? 'Domain Durum Kontrolü'       : 'Domain Status Check' },
  { icon: BarChart2,    title: lang === 'tr' ? 'Yanıt Süresi Grafikleri'     : 'Response Time Charts' },
  { icon: CalendarCheck,title: lang === 'tr' ? 'SSL & Domain Tarih Takibi'   : 'SSL & Domain Date Tracking' },
  { icon: Mail,         title: lang === 'tr' ? 'Anlık E-posta Bildirimi'     : 'Instant Email Alert' },
  { icon: CircleCheckBig, title: lang === 'tr' ? 'Ücretsiz & Ücretli — Tam Erişim' : 'Free & Paid — Full Access' },
];

export default function MonitoringFeaturesTable({ lang = 'tr' }) {
  const items = features(lang);

  return (
    <>
      <style>{`

        .mft-section {
          padding: 0rem 2rem;
          background: #ffffff;
        }

        .mft-inner {
          max-width: 960px;
          margin: 0 auto;
        }

        /* ── Featured hero ── */
        .mft-featured {
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          background: #f5f3ff;
          border: 1.5px solid #c4b5fd;
          border-radius: 16px;
          padding: 1.75rem;
          margin-bottom: 2.5rem;
          transition: box-shadow 0.25s;
        }
        .mft-featured:hover {
          box-shadow: 0 8px 28px -6px rgba(99,102,241,0.22);
        }
        .mft-feat-icon {
          flex-shrink: 0;
          width: 48px; height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 4px 12px -2px rgba(99,102,241,0.4);
        }
        .mft-feat-tag {
          display: inline-block;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #6366f1;
          background: #ede9fe;
          border-radius: 99px;
          padding: 0.2rem 0.65rem;
          margin-bottom: 0.45rem;
        }
        .mft-feat-title {
          font-size: 1.05rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.3rem;
        }
        .mft-feat-desc {
          font-size: 0.875rem;
          color: #4b5563;
          line-height: 1.7;
        }

        /* ── Section header ── */
        .mft-heading {
          font-size: 1.5rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 0.25rem;
          letter-spacing: -0.02em;
        }
        .mft-sub {
          font-size: 0.9rem;
          color: #9ca3af;
          margin-bottom: 1.5rem;
        }

        /* ── Feature list ── */
        .mft-list {
          display: flex;
          flex-direction: column;
          overflow: hidden;
          gap: 12px;
          padding: 0px 2rem;
        }

        .mft-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 1.25rem;
          border-bottom: 1px solid #f3f4f6;
          background: #fff;
          transition: background 0.15s;
          gap: 1rem;
        }
        .mft-item:last-child { border-bottom: none; }
        .mft-item:hover { background: #fafafa; }

        .mft-item-left {
          display: flex;
          align-items: center;
          gap: 0.85rem;
        }

        .mft-icon-dot {
          width: 34px; height: 34px;
          border-radius: 9px;
          background: #f3f4f6;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        .mft-item-title {
          font-size: 0.9rem;
          font-weight: 500;
          color: #1f2937;
        }

        .mft-check {
          width: 22px; height: 22px;
          border-radius: 50%;
          background: #ecfdf5;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }

        @media (max-width: 520px) {
          .mft-featured { flex-direction: column; }
          .mft-list { padding: 0; }
        }

      `}</style>

      <section className="mft-section">
        <div className="mft-inner">

          {/* Featured */}
          <div className="mft-featured">
            <div className="mft-feat-icon">
              <Camera size={22} color="white" strokeWidth={2} />
            </div>
            <div>
              <span className="mft-feat-tag">
                {lang === 'tr' ? '★ Öne Çıkan Özellik' : '★ Standout Feature'}
              </span>
              <div className="mft-feat-title">
                {lang === 'tr' ? 'Anlık Hata Ekran Görüntüsü' : 'Instant Error Screenshot'}
              </div>
              <div className="mft-feat-desc">
                {lang === 'tr'
                  ? 'Sitenizde bir hata oluştuğu anda tam ekran görüntüsü otomatik alınır. Kullanıcılarınızın ne gördüğünü siz de görün — sunucuya bağlanmadan, anında.'
                  : 'The moment your site errors, a full-page screenshot is captured automatically. See exactly what your visitors saw — instantly.'}
              </div>
            </div>
          </div>

          {/* List heading */}
          <div className="mft-heading">
            {lang === 'tr' ? 'Tüm Özellikler' : 'All Features'}
          </div>
          <div className="mft-sub">
            {lang === 'tr' ? 'Her planda tam erişim — hiçbir şey kısıtlanmaz.' : 'Full access on every plan — nothing locked.'}
          </div>

          {/* Feature rows */}
          <div className="mft-list">
            {items.map((f, i) => {
              const Icon = f.icon;
              return (
                <div className="mft-item" key={i}>
                  <div className="mft-item-left">
                    <div className="mft-icon-dot">
                      <Icon size={18} color="#6c5ce7" strokeWidth={2} />
                    </div>
                    <span className="mft-item-title">{f.title}</span>
                  </div>
                  <div className="mft-check">
                      <CircleCheckBig size={32} color="#00bf00" strokeWidth={1} />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>
    </>
  );
}
